/**
 * The layout primitive every Overview section is built from, and the formatters they share.
 *
 * `Section` used to live here too. It moved to `molecules/org-admin/OrgAdminSection` when the merged
 * Usage tab became its second consumer — see that file.
 *
 * Extracted when `OverviewPanel` reached 700 lines: the panel's job is fetching the data, gating on
 * authority and composing four sections, and it could not be read for any of those while also
 * containing the markup of all four. Each section is now its own file; these are the pieces they
 * have in common.
 */

import { OA_TWO_UP_BREAKPOINT } from '@/components/molecules/org-admin/OrgAdminTable';

/**
 * The two-up row. Stacks on a narrow PANEL, not a narrow window.
 *
 * WHY IT TAKES `width` INSTEAD OF A CSS CLASS. This was `className="oa-card-row grid gap-3"`, on the
 * belief that `.oa-card-row` carried a container query. It does not: `.oa-card-row` is the module's
 * card-style TABLE ROW primitive — `display: flex; flex-direction: column; padding: 12px 16px;
 * border-bottom: 1px` — used by the narrow-width variants of the member, invitation and billing rows.
 * At (0,2,0) it also beat Tailwind's `.grid` and `.gap-3`, so the computed style was
 * `display: flex; flex-direction: column; grid-template-columns: none; gap: 8px`.
 *
 * The consequence was that Overview's two-column layout had never once rendered — every pair stacked
 * full width at every viewport, which is why those tables sprawled with empty middles — while the
 * borrowed row padding quietly inset the pair by 16px and a stray hairline appeared under whichever
 * pair fell last in its parent.
 *
 * The mechanism here is the one AnalysisPanel already uses for exactly this decision: measure the
 * panel with `useContainerWidth` and switch at 900px. Same threshold, so the two tabs break to one
 * column at the same real width. `items-stretch` (the grid default, stated for intent) gives the two
 * cards a shared bottom edge instead of the ragged one two natural-height cards produce.
 */
export function CardRow({
  width,
  children,
}: {
  /** The measured panel width from `useContainerWidth`. `null` until the first measurement. */
  width: number | null;
  children: React.ReactNode;
}) {
  const isTwoColumn = width !== null && width >= OA_TWO_UP_BREAKPOINT;
  return (
    <div
      className={
        isTwoColumn ? 'grid grid-cols-2 items-stretch gap-3' : 'flex flex-col gap-3'
      }
    >
      {children}
    </div>
  );
}

/** Rows each ranking plots before deferring to its table view and the tab that owns the full list. */
export const RANK_ROWS = 6;

export function formatCount(value: number): string {
  return value.toLocaleString();
}

/** Axis ticks convey the level, not the cent. */
export function formatCurrencyAxis(value: number): string {
  if (Math.abs(value) >= 1000) return `$${Math.round(value / 100) / 10}k`;
  return `$${Math.round(value)}`;
}

/**
 * Cost per run is often fractions of a cent, where `formatCurrencyUsd`'s two decimal places render
 * every service as "$0.00" — which is the same as having no column at all.
 */
export function formatUnitCost(value: number): string {
  if (value === 0) return '$0.00';
  if (value < 0.01) return `$${value.toFixed(4)}`;
  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatRatio(value: number | null): string {
  return value === null ? '—' : value.toFixed(1);
}

/** Ratios are held 0–1 and read as percentages. */
export function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}
